import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Link,
  Spinner,
  Stack,
  TextField,
  Typography,
} from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useIdentity } from '../auth';
import LocusLogo from '../assets/locus-logo.png';
import LoginBackground from '../assets/login-background.svg';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { STORAGE_KEYS } from '../utils/storageKeys';
import { Storage } from '../utils/storage';
import {
  useClientId,
  hasSubdomainClientId,
  getClientIdFromUrl,
} from '../hooks/useClientId';
import { useClientMetadata, type ClientMetadataResponse } from '../api';
import {
  ApiClient,
  endpointRegistry,
  type RequestConfig,
} from '@locus-taxy/encore-ui-network';
import { getApiBaseUrl } from '../config';
import type { AxiosRequestConfig } from 'axios';
import {
  buildFormButtonTestId,
  buildFormTestId,
  getTestId,
} from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';

const LOGIN_PAGE_TEST_ENTITY = 'login-page';

async function fetchOrganizationId(
  clientId: string,
  hasSubdomain: boolean,
  metadataData: ClientMetadataResponse | null | undefined
): Promise<string | undefined> {
  if (!clientId) {
    return undefined;
  }

  if (hasSubdomain) {
    return metadataData?.data?.organizationId;
  }

  const metadataBaseUrl = getApiBaseUrl(clientId);
  const client = ApiClient.getInstance(metadataBaseUrl);
  const path = endpointRegistry.getEndpoint('client.metadata', {
    clientId,
  });
  const result = await client.get<ClientMetadataResponse>(path, {
    requireAuth: false,
  } as RequestConfig & AxiosRequestConfig);
  return result?.data?.organizationId;
}

/**
 * Strip stale Auth0 callback params (`error`, `code`, `state`, ...) from the
 * current URL so the SDK treats the next login as a fresh attempt. Returns the
 * cleaned URL.
 */
function clearStaleAuthParams(): URL {
  const url = new URL(window.location.href);
  if (url.searchParams.has('error') || url.searchParams.has('code')) {
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.pathname + url.search);
  }
  return url;
}

/** Read the legacy/local URL clientId and mirror it into storage. */
function resolveLegacyClientId(): string {
  try {
    const fromUrl = getClientIdFromUrl() ?? '';
    const existing = Storage.get(STORAGE_KEYS.clientId) ?? '';
    if (fromUrl) {
      Storage.set(STORAGE_KEYS.clientId, fromUrl);
    }
    return fromUrl || existing;
  } catch {
    // ignore storage errors
    return '';
  }
}

function saveClientIdToStorage(clientId: string): void {
  try {
    if (clientId) {
      Storage.set(STORAGE_KEYS.clientId, clientId);
    }
  } catch {
    // ignore storage errors
  }
}

/** Build the Auth0 `authorizationParams` for a login redirect. */
function buildAuthParams(
  organization: string | undefined,
  clientIdHint: string
): Record<string, string> {
  if (!organization) {
    return { prompt: 'login' };
  }
  return {
    organization,
    l_client_id: clientIdHint,
    prompt: 'login',
  };
}

type HandleLoginDeps = {
  hasSubdomain: boolean;
  isError: boolean;
  inputClientId: string;
  effectiveClientId: string;
  metadataData: ClientMetadataResponse | null | undefined;
  loginWithRedirect: (opts: {
    appState: { returnTo: string };
    authorizationParams: Record<string, string>;
  }) => Promise<void>;
};

/**
 * Resolve the organization id, swallowing metadata-fetch failures for legacy
 * URLs. Returns `undefined` to signal "abort the login" only for the subdomain
 * flow (where a failed fetch must not fall through to a login redirect).
 */
async function resolveOrganization(deps: HandleLoginDeps): Promise<{
  organization: string | undefined;
  abort: boolean;
}> {
  const { effectiveClientId, hasSubdomain, metadataData } = deps;
  try {
    const organization = await fetchOrganizationId(
      effectiveClientId,
      hasSubdomain,
      metadataData
    );
    return { organization, abort: false };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching client metadata:', error);
    return { organization: undefined, abort: hasSubdomain };
  }
}

/** Run the full login flow. Extracted so the callback stays trivial. */
async function performLogin(deps: HandleLoginDeps): Promise<void> {
  const { hasSubdomain, isError, inputClientId, effectiveClientId } = deps;
  if (hasSubdomain && isError) {
    return;
  }

  const url = clearStaleAuthParams();

  // For legacy URLs, save clientId to storage if provided
  if (!hasSubdomain) {
    saveClientIdToStorage(inputClientId);
  }

  const { organization, abort } = await resolveOrganization(deps);
  if (abort) {
    return;
  }

  void deps.loginWithRedirect({
    appState: { returnTo: url.pathname + url.search },
    authorizationParams: buildAuthParams(
      organization,
      effectiveClientId || inputClientId
    ),
  });
}

/**
 * Encapsulates the login click handler. Lives outside the component so its
 * branching (subdomain vs legacy, metadata fetch failure handling) does not
 * inflate LoginPage's cyclomatic complexity.
 */
function useHandleLogin(deps: HandleLoginDeps) {
  const {
    hasSubdomain,
    isError,
    inputClientId,
    effectiveClientId,
    metadataData,
    loginWithRedirect,
  } = deps;

  return useCallback((): void => {
    void performLogin({
      hasSubdomain,
      isError,
      inputClientId,
      effectiveClientId,
      metadataData,
      loginWithRedirect,
    });
  }, [
    hasSubdomain,
    isError,
    inputClientId,
    effectiveClientId,
    loginWithRedirect,
    metadataData,
  ]);
}

/**
 * Mirror the legacy/local-URL clientId into component state. Extracted from
 * LoginPage to keep the component's cyclomatic complexity low.
 */
function useSyncLegacyClientId(
  hasSubdomain: boolean,
  setInputClientId: (id: string) => void
) {
  useEffect(() => {
    if (hasSubdomain) {
      return;
    }
    const resolved = resolveLegacyClientId();
    if (resolved) {
      setInputClientId(resolved);
    }
  }, [hasSubdomain, setInputClientId]);
}

function useAuth0ErrorMessage() {
  const { t } = useTranslation();
  const { error: auth0Error } = useIdentity();

  return useMemo(() => {
    if (!auth0Error) {
      return null;
    }
    const msg = auth0Error.message ?? '';
    if (msg.includes('SSO_MIGRATED')) {
      return t(
        'dashboard.loginPage.errors.ssoMigrated',
        "Your account has been migrated to Single Sign-On (SSO). Please close this window and sign in using your company's SSO login."
      );
    }
    if (msg.includes('user is blocked')) {
      return t(
        'dashboard.loginPage.errors.userBlocked',
        'Your account has been blocked. Please contact your administrator.'
      );
    }
    return t(
      'dashboard.loginPage.errors.unknown',
      'Login failed. Please contact your administrator.'
    );
  }, [auth0Error, t]);
}

/**
 * Encapsulate the client-metadata fetch wiring (which clientId to query and
 * whether the query is enabled). Extracted so its branching does not inflate
 * LoginPage's cyclomatic complexity.
 */
function useLoginMetadata(
  hasSubdomain: boolean,
  clientId: string,
  effectiveClientId: string
) {
  const metadataClientId = hasSubdomain ? clientId : effectiveClientId;
  return useClientMetadata(metadataClientId || null, {
    enabled: hasSubdomain ? Boolean(clientId) : false,
  });
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { loginWithRedirect } = useIdentity();
  const [inputClientId, setInputClientId] = useState('');

  const hostname = window.location.hostname;
  const hasSubdomain = hasSubdomainClientId(hostname);
  const clientId = useClientId();
  const effectiveClientId = clientId || inputClientId || '';

  const auth0ErrorMessage = useAuth0ErrorMessage();

  const {
    isLoading,
    isError,
    data: metadataData,
  } = useLoginMetadata(hasSubdomain, clientId, effectiveClientId);

  // For legacy/local URLs (e.g. https://local.<client>.<stack>.<domain>/clientId/<id>),
  // the clientId in the URL is the source of truth; mirror it into local state.
  useSyncLegacyClientId(hasSubdomain, setInputClientId);

  const handleLogin = useHandleLogin({
    hasSubdomain,
    isError,
    inputClientId,
    effectiveClientId,
    metadataData,
    loginWithRedirect,
  });

  // Used as both the placeholder and the accessible name: the field is
  // label-less by design, and a placeholder alone is not an accessible name.
  const clientIdLabel = t('dashboard.loginPage.enterClientId', 'Client ID');

  return (
    <Box className="flex min-h-screen flex-col sm:flex-row">
      {/* Illustration: an aspect-ratio banner above the form on small screens
          (so the artwork keeps its proportions rather than being cropped to a
          strip), a full-height column beside it from `sm` up. */}
      <Box
        className="aspect-[16/10] w-full shrink-0 sm:aspect-auto sm:h-auto sm:w-[58%]"
        style={{
          backgroundImage: `url(${LoginBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'image',
          entity: 'login-illustration',
          page: LOGIN_PAGE_TEST_ENTITY,
        })}
      />
      <Box className="flex flex-1 flex-col p-[clamp(1.25rem,3vw,3rem)]">
        <Box className="flex flex-1 items-center justify-center">
          <Stack direction="column" gap={6} className="w-full max-w-[400px]">
            <img
              src={LocusLogo}
              alt={t('dashboard.loginPage.locusLogoAlt', 'Locus Logo')}
              style={{
                height: 'clamp(26px, 2.4vw, 36px)',
                alignSelf: 'flex-start',
              }}
              data-testid={getTestId({
                action: TestIdAction.VIEW,
                object: 'image',
                entity: 'locus-logo',
                page: LOGIN_PAGE_TEST_ENTITY,
              })}
            />
            <Typography
              variant="h2"
              className="text-[clamp(1.375rem,2.6vw,2.5rem)] leading-tight"
            >
              {t(
                'dashboard.loginPage.welcome',
                'Welcome to the most advanced digital supply chain optimization engine'
              )}
            </Typography>
            {auth0ErrorMessage && (
              <Alert variant="warning" className="w-full">
                <AlertDescription>{auth0ErrorMessage}</AlertDescription>
              </Alert>
            )}
            {!hasSubdomain && (
              <TextField
                placeholder={clientIdLabel}
                aria-label={clientIdLabel}
                className="w-full"
                value={inputClientId}
                onChange={e => setInputClientId(e.target.value)}
                data-testid={buildFormTestId({
                  fieldType: 'text',
                  fieldName: 'client-id',
                  entity: LOGIN_PAGE_TEST_ENTITY,
                  formAction: 'submit',
                })}
              />
            )}
            <Button
              size="lg"
              className="w-full"
              disabled={isLoading || (hasSubdomain && isError)}
              onClick={handleLogin}
              data-testid={buildFormButtonTestId({
                kind: 'submit-form',
                entity: LOGIN_PAGE_TEST_ENTITY,
                formAction: 'submit',
              })}
            >
              {isLoading && (
                <Spinner
                  size="sm"
                  className="mr-2"
                  aria-hidden="true"
                  aria-label=""
                />
              )}
              {t('dashboard.loginPage.login', 'Login')}
            </Button>
          </Stack>
        </Box>
        <Stack
          direction="row"
          gap={6}
          className="flex-wrap justify-end text-[clamp(0.8125rem,1vw,0.875rem)]"
        >
          <Link
            href="https://locus.sh/documents/MaraLabsTermsOfUse.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[length:inherit]"
          >
            {t('dashboard.loginPage.termsOfUse', 'Terms of use')}
          </Link>
        </Stack>
      </Box>
    </Box>
  );
}
